import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaGoogle } from 'react-icons/fa';
import './AuthPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const { signup, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  
  // Get package data from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const packageData = urlParams.get('plan') ? {
    plan: urlParams.get('plan'),
    duration: urlParams.get('duration'),
    price: parseInt(urlParams.get('price')) || 0
  } : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (authError) {
      setAuthError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setLoading(true);
    setAuthError('');
    
    try {
      await signup(formData.name, formData.email, formData.password);
      // Check if there's package data in URL parameters
      if (packageData) {
        // Redirect to booking page with the package data from URL
        navigate('/booking', { state: { package: packageData } });
      } else {
        // Redirect to home page after successful signup
        navigate('/');
      }
    } catch (error) {
      setAuthError(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    setAuthError('');
    
    try {
      await signInWithGoogle();
      // Check if there's package data in URL parameters
      if (packageData) {
        // Redirect to booking page with the package data from URL
        navigate('/booking', { state: { package: packageData } });
      } else {
        // Redirect to home page after successful signup
        navigate('/');
      }
    } catch (error) {
      setAuthError(error.message || 'Failed to sign up with Google');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <h1 className="auth-title">Create Account</h1>
              <p className="auth-subtitle">Join Skyline Backpackers today</p>
            </div>
            
            <form className="auth-form" onSubmit={handleSubmit}>
              {authError && <div className="error-message" style={{ textAlign: 'center', marginBottom: '1rem' }}>{authError}</div>}
              
              <div className="auth-form-group">
                <label htmlFor="name" className="auth-form-label">Full Name</label>
                <FaUser className="input-icon" />
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="auth-form-input"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  autoComplete="name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
              
              <div className="auth-form-group">
                <label htmlFor="email" className="auth-form-label">Email</label>
                <FaEnvelope className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="auth-form-input"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              
              <div className="auth-form-group">
                <label htmlFor="password" className="auth-form-label">Password</label>
                <FaLock className="input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  className="auth-form-input"
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>
              
              <div className="auth-form-group">
                <label htmlFor="confirmPassword" className="auth-form-label">Confirm Password</label>
                <FaLock className="input-icon" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  className="auth-form-input"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
                {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
              </div>
              
              <button 
                type="submit" 
                className="auth-button"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>
              
              <div className="divider">
                <span>or</span>
              </div>
              
              <button 
                type="button" 
                className="google-button"
                onClick={handleGoogleSignup}
                disabled={loading}
              >
                <FaGoogle className="google-icon" /> 
                Sign up with Google
              </button>
            </form>
            
            <div className="auth-footer">
              <p>Already have an account? <Link to="/login" className="auth-link">Sign In</Link></p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SignupPage;
